import * as React from 'react';
import { ApiHatasi, api } from '@/lib/api';
import { anMs } from '@/lib/utils';

/**
 * Takvim paylasimi (iCal).
 *
 * Kanal yoneticisi entegrasyonu gelene kadar cift satisi engellemenin en
 * ucuz yolu. Fiyat tasimaz — yalnizca dolu tarihleri esitler.
 */

interface Besleme {
  id: string;
  ad: string;
  url: string;
  aktif: boolean;
  sonCekim: string | number | null;
  sonSonuc: string | null;
  sonOlaySayisi: number | null;
}

interface Oda {
  id: string;
  no: string;
  tipAd: string | null;
  disaAktarimUrl: string | null;
  beslemeler: Besleme[];
}

const anEtiket = (deger: string | number | null) => {
  const ms = anMs(deger);
  if (ms === null) return 'henüz çekilmedi';
  return new Date(ms).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const GIRDI =
  'w-full rounded-xl border-[1.5px] border-cizgi bg-white px-3.5 py-2.5 text-[15px] text-lacivert outline-none transition focus:border-marka-600 focus:shadow-[0_0_0_4px_rgba(31,79,216,.12)]';

export function Takvim({ yetkiler }: { yetkiler: string[] }) {
  const yazabilir = yetkiler.includes('room:write');
  const [odalar, setOdalar] = React.useState<Oda[]>([]);
  const [yukleniyor, setYukleniyor] = React.useState(true);
  const [hata, setHata] = React.useState<string | null>(null);
  const [bilgi, setBilgi] = React.useState<string | null>(null);
  const [islenen, setIslenen] = React.useState<string | null>(null);
  const [ekleAcik, setEkleAcik] = React.useState<Oda | null>(null);
  const [kopyalanan, setKopyalanan] = React.useState<string | null>(null);

  const yukle = React.useCallback(async () => {
    try {
      setOdalar((await api<{ odalar: Oda[] }>('/api/takvim')).odalar);
      setHata(null);
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Takvim bilgisi yüklenemedi.');
    } finally {
      setYukleniyor(false);
    }
  }, []);

  React.useEffect(() => {
    void yukle();
  }, [yukle]);

  function duyur(mesaj: string) {
    setBilgi(mesaj);
    setTimeout(() => setBilgi(null), 4000);
  }

  async function paylasimAc(oda: Oda, yenile = false) {
    setIslenen(oda.id);
    try {
      await api(`/api/takvim/odalar/${oda.id}/paylasim`, { method: 'POST', body: JSON.stringify({ yenile }) });
      await yukle();
      duyur(yenile ? `Oda ${oda.no} adresi yenilendi — eski adres artık çalışmıyor.` : `Oda ${oda.no} takvimi paylaşıma açıldı.`);
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Adres üretilemedi.');
    } finally {
      setIslenen(null);
    }
  }

  async function cek(b: Besleme) {
    setIslenen(b.id);
    try {
      const s = await api<{ olaySayisi: number; basarili: boolean }>(`/api/takvim/beslemeler/${b.id}/cek`, {
        method: 'POST',
      });
      await yukle();
      duyur(s.basarili ? `${b.ad}: ${s.olaySayisi} olay alındı.` : `${b.ad} çekilemedi.`);
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Çekilemedi.');
    } finally {
      setIslenen(null);
    }
  }

  async function sil(b: Besleme) {
    setIslenen(b.id);
    try {
      await api(`/api/takvim/beslemeler/${b.id}`, { method: 'DELETE' });
      await yukle();
      duyur(`${b.ad} kaldırıldı; getirdiği bloklar da silindi.`);
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Silinemedi.');
    } finally {
      setIslenen(null);
    }
  }

  if (yukleniyor) return <div className="h-72 animate-pulse rounded-[20px] bg-white" />;

  return (
    <div className="flex flex-col gap-[18px] [animation:rise_.4s_both]">
      <div className="rounded-[16px] bg-mavi-zemin px-4 py-3.5 text-[13px] leading-relaxed text-lacivert">
        Takvim paylaşımı <strong>fiyat göndermez</strong>, yalnızca dolu tarihleri eşitler — ama Airbnb ile aynı
        odayı iki kez satmanızı engeller. Her odanın kendi adresi vardır: bu adresi Airbnb/Booking paneline
        yapıştırırsınız, onların adresini de buraya eklersiniz. Beslemeler saatte bir otomatik çekilir.
      </div>

      {hata && (
        <p role="alert" className="rounded-2xl border border-kirmizi/30 bg-[#fdeceb] px-4 py-3 text-sm font-medium text-kirmizi">
          {hata}
        </p>
      )}
      {bilgi && <p className="rounded-2xl bg-yesil-zemin px-4 py-3 text-sm font-medium text-yesil">{bilgi}</p>}

      <div className="flex flex-col gap-3">
        {odalar.map((oda, i) => (
          <div
            key={oda.id}
            className="flex flex-col gap-3.5 rounded-[18px] border border-cizgi bg-white p-[18px] [animation:popin_.4s_cubic-bezier(.2,.8,.2,1)_both]"
            style={{ animationDelay: `${i * 30}ms` }}
          >
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-[17px] font-bold text-lacivert">{oda.no}</span>
              <span className="text-[13px] text-gri">{oda.tipAd ?? '—'}</span>
              {oda.beslemeler.length > 0 && (
                <span className="rounded-full bg-mavi-zemin px-2.5 py-1 text-[11px] font-bold text-marka-600">
                  {oda.beslemeler.length} besleme
                </span>
              )}
              {yazabilir && (
                <button
                  onClick={() => setEkleAcik(oda)}
                  className="ml-auto rounded-full border-[1.5px] border-cizgi bg-white px-3.5 py-2 text-[13px] font-semibold text-lacivert transition hover:border-lacivert"
                >
                  Kanal takvimi ekle
                </button>
              )}
            </div>

            {/* Disa aktarim */}
            <div className="flex flex-col gap-2 rounded-[14px] bg-zemin p-3.5">
              <span className="text-[12px] font-bold uppercase tracking-[.06em] text-gri">
                Bu odanın takvim adresi
              </span>
              {oda.disaAktarimUrl ? (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <code className="min-w-0 flex-1 overflow-x-auto rounded-lg bg-white px-3 py-2 text-[12px] text-lacivert">
                      {oda.disaAktarimUrl}
                    </code>
                    <button
                      onClick={async () => {
                        await navigator.clipboard.writeText(oda.disaAktarimUrl!).catch(() => undefined);
                        setKopyalanan(oda.id);
                        setTimeout(() => setKopyalanan(null), 1600);
                      }}
                      className="rounded-full bg-marka-600 px-3.5 py-2 text-[13px] font-semibold text-white transition hover:bg-marka-700"
                    >
                      {kopyalanan === oda.id ? 'Kopyalandı' : 'Kopyala'}
                    </button>
                  </div>
                  <p className="text-[12.5px] leading-relaxed text-gri">
                    Bu adres şifre istemez; Airbnb anonim çeker. İçinde <strong>misafir adı, fiyat ve rezervasyon
                    kodu yoktur</strong> — sadece dolu tarihler.
                    {yazabilir && (
                      <>
                        {' '}
                        <button
                          onClick={() => paylasimAc(oda, true)}
                          disabled={islenen === oda.id}
                          className="font-semibold text-marka-600 hover:underline disabled:opacity-40"
                        >
                          Adresi yenile
                        </button>{' '}
                        (eskisi çalışmaz hale gelir)
                      </>
                    )}
                  </p>
                </>
              ) : (
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-[13px] text-gri">Paylaşım kapalı.</span>
                  {yazabilir && (
                    <button
                      onClick={() => paylasimAc(oda)}
                      disabled={islenen === oda.id}
                      className="rounded-full bg-lacivert px-3.5 py-2 text-[13px] font-semibold text-white transition hover:bg-marka-600 disabled:opacity-40"
                    >
                      Paylaşımı aç
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Ice aktarim */}
            {oda.beslemeler.map((b) => (
              <div key={b.id} className="flex flex-wrap items-center gap-3 border-t border-satir pt-3">
                <div className="flex min-w-[200px] flex-1 flex-col gap-0.5">
                  <span className="text-[14px] font-semibold text-lacivert">{b.ad}</span>
                  <span className="truncate text-[12px] text-gri">{b.url}</span>
                </div>
                <div className="flex min-w-[180px] flex-col gap-0.5">
                  <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-gri">Son çekim</span>
                  <span
                    className="text-[12.5px]"
                    style={{ color: b.sonSonuc?.startsWith('Hata') ? '#e5484d' : '#0b1530' }}
                  >
                    {anEtiket(b.sonCekim)}
                    {b.sonSonuc ? ` · ${b.sonSonuc}` : ''}
                  </span>
                </div>
                {yazabilir && (
                  <div className="ml-auto flex gap-2">
                    <button
                      onClick={() => cek(b)}
                      disabled={islenen === b.id}
                      className="rounded-full border-[1.5px] border-cizgi bg-white px-3.5 py-2 text-[13px] font-semibold text-lacivert transition hover:border-lacivert disabled:opacity-40"
                    >
                      Şimdi çek
                    </button>
                    <button
                      onClick={() => sil(b)}
                      disabled={islenen === b.id}
                      className="rounded-full border-[1.5px] border-cizgi bg-white px-3.5 py-2 text-[13px] font-semibold text-kirmizi transition hover:border-kirmizi disabled:opacity-40"
                    >
                      Kaldır
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>

      {ekleAcik && (
        <BeslemeEkle
          oda={ekleAcik}
          onKapat={() => setEkleAcik(null)}
          onEklendi={async (mesaj) => {
            setEkleAcik(null);
            duyur(mesaj);
            await yukle();
          }}
        />
      )}
    </div>
  );
}

function BeslemeEkle({
  oda,
  onKapat,
  onEklendi,
}: {
  oda: Oda;
  onKapat: () => void;
  onEklendi: (mesaj: string) => void;
}) {
  const [ad, setAd] = React.useState('Airbnb');
  const [url, setUrl] = React.useState('');
  const [hata, setHata] = React.useState<string | null>(null);
  const [gonderiliyor, setGonderiliyor] = React.useState(false);

  React.useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && !gonderiliyor && onKapat();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onKapat, gonderiliyor]);

  async function gonder(e: React.FormEvent) {
    e.preventDefault();
    setGonderiliyor(true);
    setHata(null);
    try {
      const s = await api<{ basarili: boolean; olaySayisi: number; hata?: string }>('/api/takvim/beslemeler', {
        method: 'POST',
        body: JSON.stringify({ roomId: oda.id, ad: ad.trim(), url: url.trim() }),
      });
      onEklendi(
        s.basarili
          ? `${ad} eklendi · ${s.olaySayisi} dolu tarih alındı.`
          : `${ad} eklendi ama takvim çekilemedi: ${s.hata ?? 'bilinmeyen hata'}`,
      );
    } catch (err) {
      setHata(err instanceof ApiHatasi ? err.message : 'Eklenemedi.');
      setGonderiliyor(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-lacivert/60 [animation:fadein_.2s_both]" onClick={onKapat} role="presentation">
      <div className="flex min-h-full items-center justify-center p-4">
        <form
          onSubmit={gonder}
          onClick={(e) => e.stopPropagation()}
          className="flex w-full max-w-[500px] flex-col gap-4 rounded-[20px] bg-white p-6 shadow-[0_40px_80px_-32px_rgba(11,21,48,.55)] [animation:popin_.3s_cubic-bezier(.2,.8,.2,1)_both]"
        >
          <div>
            <h2 className="text-[20px] font-bold tracking-[-.01em] text-lacivert">Oda {oda.no} · kanal takvimi</h2>
            <p className="mt-0.5 text-[13px] leading-relaxed text-gri">
              Kanalın yayınladığı takvim adresini yapıştırın. O kanalda satılan tarihler burada otomatik kapanır.
            </p>
          </div>

          <label className="flex flex-col gap-1.5">
            <span className="text-[12.5px] font-semibold text-gri">Kanal adı</span>
            <select value={ad} onChange={(e) => setAd(e.target.value)} className={GIRDI}>
              {['Airbnb', 'Booking.com', 'Vrbo', 'Diğer'].map((k) => (
                <option key={k} value={k}>{k}</option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-[12.5px] font-semibold text-gri">Takvim adresi (.ics)</span>
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://www.airbnb.com/calendar/ical/..."
              autoFocus
              required
              className={GIRDI}
            />
          </label>

          <p className="rounded-xl bg-zemin px-3.5 py-3 text-[12.5px] leading-relaxed text-gri">
            Airbnb'de: <strong className="text-lacivert">İlan → Takvim → Kullanılabilirlik → Takvimleri
            senkronize et → Takvimi dışa aktar</strong>. Adres <code>https://</code> ile başlamalı.
          </p>

          {hata && <p role="alert" className="rounded-xl bg-[#fdeceb] px-3.5 py-3 text-[13px] font-medium text-kirmizi">{hata}</p>}

          <div className="mt-1 flex gap-3">
            <button
              type="button"
              onClick={onKapat}
              disabled={gonderiliyor}
              className="flex-1 rounded-full border-[1.5px] border-cizgi bg-white py-3 text-[15px] font-semibold text-lacivert transition hover:border-gri"
            >
              Vazgeç
            </button>
            <button
              type="submit"
              disabled={gonderiliyor || !url.trim()}
              className="flex flex-1 items-center justify-center gap-2 rounded-full bg-marka-600 py-3 text-[15px] font-semibold text-white transition hover:bg-marka-700 disabled:opacity-40"
            >
              {gonderiliyor && <span className="size-4 rounded-full border-2 border-white/35 border-t-white [animation:spin_.7s_linear_infinite]" />}
              {gonderiliyor ? 'Ekleniyor…' : 'Ekle ve çek'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
